import React from "react";
import Counterpart from "counterpart";
import DOMPurify from "dompurify";

type TranslateProps = {
    content: string;
    component?: keyof JSX.IntrinsicElements | React.ComponentType<any>;
    with?: Record<string, any>;
    unsafe?: boolean;
    [key: string]: any;
};

const buildNodes = (template: string, values: Record<string, any>): React.ReactNode[] => {
    const tokenPattern = /%\(([^)]+)\)s/g;
    const nodes: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = tokenPattern.exec(template)) !== null) {
        const [fullMatch, key] = match;
        const start = match.index;

        if (start > lastIndex) {
            nodes.push(template.slice(lastIndex, start));
        }

        const value = values[key];
        if (React.isValidElement(value)) {
            nodes.push(React.cloneElement(value, { key: `${key}-${start}` }));
        } else if (value !== undefined && value !== null) {
            nodes.push(String(value));
        } else {
            nodes.push(fullMatch);
        }

        lastIndex = start + fullMatch.length;
    }

    if (lastIndex < template.length) {
        nodes.push(template.slice(lastIndex));
    }

    return nodes;
};

const Translate: React.FC<TranslateProps> = ({ content, component = "span", with: values = {}, unsafe = false, ...rest }) => {
    const translated = Counterpart.translate(content, values);
    const Tag = component as any;

    if (unsafe) {
        const html = DOMPurify.sanitize(String(translated));
        return <Tag {...rest} dangerouslySetInnerHTML={{ __html: html }} />;
    }

    const hasReactValues = Object.values(values).some((value) => React.isValidElement(value));
    if (hasReactValues) {
        return <Tag {...rest}>{buildNodes(String(translated), values)}</Tag>;
    }

    return <Tag {...rest}>{translated as React.ReactNode}</Tag>;
};

export default Translate;
